import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { X, Upload, MapPin } from "lucide-react";
import { toast } from "sonner";
import { createEvent, uploadImage } from "@/lib/mapioApi";
import { CATEGORIES, DEFAULT_CENTER, FALLBACK_LOGO } from "@/lib/mapioConstants";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";

const pinIcon = L.divIcon({
  className: "mapio-pin",
  html: `<div class="pin-wrapper"><div style="width:100%;height:100%;border-radius:50%;background:linear-gradient(135deg,#2563eb,#7c3aed);"></div></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const LocationPicker = ({ latLng, setLatLng }) => {
  useMapEvents({
    click(e) {
      setLatLng([e.latlng.lat, e.latlng.lng]);
    },
  });
  return latLng ? <Marker position={latLng} icon={pinIcon} /> : null;
};

const initialForm = {
  name: "",
  category: "Parties",
  host_name: "",
  host_phone: "",
  host_whatsapp: "",
  host_instagram: "",
  ticket_price: "",
  event_date: "",
  event_time: "",
  location_area: "",
  description: "",
  safety_note: "",
  age_restriction: false,
};

export default function AddEventModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [latLng, setLatLng] = useState(DEFAULT_CENTER);
  const [logo, setLogo] = useState("");
  const [cover, setCover] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (f) setLogo(f);
  };
  const handleCover = (e) => {
    const f = e.target.files?.[0];
    if (f) setCover(f);
  };
  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setPhotos(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.host_name || !form.event_date) {
      toast.error("Please fill name, host and date.");
      return;
    }
    // Gate behind login
    if (!user) {
      setShowAuth(true);
      return;
    }
    setSubmitting(true);
    try {
      let logoUrl = FALLBACK_LOGO;
      let coverUrl = FALLBACK_LOGO;
      let photoUrls = [];

      try {
        if (logo) logoUrl = await uploadImage(logo);
        if (cover) coverUrl = await uploadImage(cover);
        if (photos.length > 0) photoUrls = await Promise.all(photos.map(uploadImage));
      } catch (uploadErr) {
        console.error("Image upload failed:", uploadErr);
        toast.error(`Image upload failed: ${uploadErr.message || "Please try again."}`);
        setSubmitting(false);
        return;
      }

      await createEvent({
        ...form,
        ticket_price: form.ticket_price || "Free",
        lat: latLng[0],
        lng: latLng[1],
        logo_image: logoUrl,
        cover_image: coverUrl,
        photos: photoUrls,
      });
      setSubmitted(true);
      toast.success("Event submitted for review");
      setTimeout(() => {
        setSubmitted(false);
        setForm(initialForm);
        setLogo(""); setCover(""); setPhotos([]);
        setLatLng(DEFAULT_CENTER);
        onCreated?.();
      }, 1800);
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm text-gray-900 placeholder:text-gray-400 outline-none";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[900] bg-black/40 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
          onClick={onClose}
          data-testid="add-event-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-2xl h-full sm:h-[90vh] sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>
                  List a new event
                </h2>
                <p className="text-xs text-gray-500">Submitted events go through quick admin review.</p>
              </div>
              <button
                data-testid="close-add-modal-btn"
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white text-2xl mb-4">
                  ✓
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>
                  Submitted!
                </h3>
                <p className="text-sm text-gray-600 max-w-sm">
                  Your event has been submitted for review. It will appear on the map after admin approval.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex-1 overflow-y-auto px-6 py-5 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Event name *</label>
                    <input data-testid="form-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Hauz Khas Rooftop Soirée" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Category</label>
                    <select data-testid="form-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                      {CATEGORIES.filter((c) => c.id !== "All").map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ticket price</label>
                    <input data-testid="form-price" className={inputCls} value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} placeholder="₹499 or Free" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Host name *</label>
                    <input data-testid="form-host" className={inputCls} value={form.host_name} onChange={(e) => set("host_name", e.target.value)} placeholder="Aarav Mehra" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Host phone</label>
                    <input className={inputCls} value={form.host_phone} onChange={(e) => set("host_phone", e.target.value)} placeholder="+91…" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">WhatsApp</label>
                    <input className={inputCls} value={form.host_whatsapp} onChange={(e) => set("host_whatsapp", e.target.value)} placeholder="+91…" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Instagram</label>
                    <input className={inputCls} value={form.host_instagram} onChange={(e) => set("host_instagram", e.target.value)} placeholder="@handle" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date *</label>
                    <input data-testid="form-date" type="date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Time</label>
                    <input type="time" className={inputCls} value={form.event_time} onChange={(e) => set("event_time", e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Location area (public)</label>
                    <input data-testid="form-area" className={inputCls} value={form.location_area} onChange={(e) => set("location_area", e.target.value)} placeholder="Hauz Khas Village, Delhi" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> Pick map location
                    </label>
                    <div className="h-48 rounded-2xl overflow-hidden border border-gray-200">
                      <MapContainer center={latLng} zoom={11} style={{ width: "100%", height: "100%" }}>
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                        <LocationPicker latLng={latLng} setLatLng={setLatLng} />
                      </MapContainer>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Tap on the map to set the public location.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Description</label>
                    <textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell guests what makes this event special…" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Logo (small)</label>
                    <label className="flex items-center justify-center gap-2 cursor-pointer rounded-xl border border-dashed border-gray-300 py-3 text-xs text-gray-600 hover:bg-gray-50">
                      <Upload className="w-3.5 h-3.5" />
                      {logo ? "Change logo" : "Upload"}
                      <input type="file" accept="image/*" onChange={handleLogo} className="hidden" />
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cover image</label>
                    <label className="flex items-center justify-center gap-2 cursor-pointer rounded-xl border border-dashed border-gray-300 py-3 text-xs text-gray-600 hover:bg-gray-50">
                      <Upload className="w-3.5 h-3.5" />
                      {cover ? "Change cover" : "Upload"}
                      <input type="file" accept="image/*" onChange={handleCover} className="hidden" />
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Event photos (up to 5)</label>
                    <label className="flex items-center justify-center gap-2 cursor-pointer rounded-xl border border-dashed border-gray-300 py-3 text-xs text-gray-600 hover:bg-gray-50">
                      <Upload className="w-3.5 h-3.5" />
                      {photos.length ? `${photos.length} selected` : "Upload"}
                      <input type="file" accept="image/*" multiple onChange={handlePhotos} className="hidden" />
                    </label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Safety note / rules</label>
                    <input className={inputCls} value={form.safety_note} onChange={(e) => set("safety_note", e.target.value)} placeholder="ID required, no outside drinks…" />
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-2">
                    <input id="age-toggle" type="checkbox" checked={form.age_restriction} onChange={(e) => set("age_restriction", e.target.checked)} className="w-4 h-4 accent-blue-600" />
                    <label htmlFor="age-toggle" className="text-sm text-gray-700">18+ age restriction</label>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-amber-800">
                  Mapix does not process payments. You will receive contact requests directly from guests.
                </div>

                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "List Event"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Auth modal - shown when user tries to publish without being logged in */}
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onAuth={() => setShowAuth(false)}
      />
    </AnimatePresence>
  );
}
